import { useState, useMemo } from "react"
import { Button, Spinner, Alert, Dropdown } from "react-bootstrap"
import { FiCopy } from "react-icons/fi"
import {
  useAllMainSiteContent,
  useDeleteMainSiteContent,
  useUpdateMainSiteContent,
} from "../../../api/main-site-content/main-site-content.hooks"
import Pagination from "../../components/Pagination/Pagination"
import PageEditor from "./_components/PageEditor"
import DeleteModal from "../../components/DeleteModal"

const MainPages = () => {
  const itemsPerPage = 10
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(0)
  const [showEditor, setShowEditor] = useState(false)
  const [selectedPage, setSelectedPage] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [pageToDelete, setPageToDelete] = useState(null)
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  })

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant })
    setTimeout(
      () => setAlert({ show: false, message: "", variant: "info" }),
      5000
    )
  }

  const {
    updateContent,
    isLoading: isUpdating,
    isError: isUpdateError,
  } = useUpdateMainSiteContent()

  const {
    deleteContent,
    isLoading: isDeleting,
    isError: isDeleteError,
  } = useDeleteMainSiteContent()

  const {
    content: mainPagesContent,
    isLoading,
    isError,
    refetch,
  } = useAllMainSiteContent({ type: "main-page" })

  // Transform API data for table display
  const tableData = useMemo(() => {
    if (!mainPagesContent) return []

    return mainPagesContent.map((item) => ({
      id: item._id,
      siteName: item.siteName || "-",
      pageName: item.pageName || "-",
      type: item.type || "main-page",
      fullItem: item,
    }))
  }, [mainPagesContent])

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return tableData

    return tableData.filter((item) =>
      [item.siteName, item.pageName, item.type]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  }, [tableData, searchTerm])

  // Paginate filtered data
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, currentPage, itemsPerPage])

  const handleEdit = (item) => {
    setSelectedPage(item)
    setShowEditor(true)
  }

  const handleDelete = async () => {
    if (!pageToDelete) return

    try {
      const success = await deleteContent(pageToDelete.id)
      if (success) {
        await refetch()
        setShowDeleteModal(false)
        setPageToDelete(null)
        showAlert("Page deleted successfully!", "success")
      }
    } catch (error) {
      showAlert("Failed to delete page.", "danger")
    }
  }

  const handleDeleteClick = (item) => {
    setPageToDelete(item)
    setShowDeleteModal(true)
  }

  const handleSave = async (updatedData) => {
    if (selectedPage) {
      await updateContent(selectedPage._id, updatedData)
      setShowEditor(false)
      showAlert("Changes saved successfully!", "success")
      await refetch()
    }
  }

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      showAlert("Copied to clipboard!", "success")
    } catch (err) {
      showAlert("Failed to copy!", "danger")
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner animation='border' />
      </div>
    )
  }

  if (isError) {
    return (
      <Alert variant='danger' className='m-3'>
        <Alert.Heading>Error!</Alert.Heading>
        <p>Failed to load main pages content. Please try again later.</p>
      </Alert>
    )
  }

  if (showEditor && selectedPage) {
    return (
      <PageEditor
        pageType={selectedPage.pageName}
        pageData={selectedPage}
        onSave={handleSave}
        isLoading={isUpdating}
        onCancel={() => setShowEditor(false)}
      />
    )
  }

  return (
    <div className='col-12'>
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: "", variant: "info" })
          }
          className='mb-3'
        >
          {alert.message}
        </Alert>
      )}
      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h4 className='card-title m-0'>Main Pages Content</h4>
          <div className='d-flex gap-2'>
            <input
              type='text'
              className='form-control w-auto'
              placeholder='Search content'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className='card-body'>
          <div className='table-responsive'>
            <table className='table table-striped'>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Page Name</th>
                  <th>Type</th>
                  <th>Url</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, i) => (
                  <tr key={item.id}>
                    <td>{currentPage * itemsPerPage + i + 1}</td>
                    <td>{item.pageName}</td>
                    <td>{item.type}</td>
                    <td className='d-flex align-items-center gap-2'>
                      <span>/{item.pageName}</span>
                      <Button
                        variant='link'
                        onClick={() => handleCopy(`/${item.pageName}`)}
                        title='Copy URL'
                      >
                        <FiCopy />
                      </Button>
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant='link'>
                          &#x2022;&#x2022;&#x2022;
                        </Dropdown.Toggle>
                        <Dropdown.Menu align='end'>
                          <Dropdown.Item
                            onClick={() => handleEdit(item.fullItem)}
                          >
                            Edit
                          </Dropdown.Item>
                          {/* <Dropdown.Item onClick={() => handleDeleteClick(item)}>
                            Delete
                          </Dropdown.Item> */}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredData.length}
            onPageChange={handlePageChange}
          />

          {filteredData.length === 0 && !isLoading && (
            <div className='text-center py-4'>
              <p className='text-muted'>No content found</p>
            </div>
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setPageToDelete(null)
        }}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title='Delete Page'
        message={
          pageToDelete
            ? `Are you sure you want to delete the page "${pageToDelete.pageName}"? This action cannot be undone.`
            : "Are you sure you want to delete this page?"
        }
      />
    </div>
  )
}

export default MainPages
